import { Button, Grid, Group, Paper, Radio, Select, SimpleGrid, Stack, Text, Title } from "@mantine/core";
import { IconCircleCheckFilled } from "@tabler/icons-react";
import { useState } from "react";
import { ConsentConfig } from "../../../Models/consentConfig";
import { defaultBannerContent, finalConsentConfig, layouts, themesConfig } from "../../../utils/utils";
import { useTranslation } from 'react-i18next';

interface ConsentConfigurationProps {
    handleExpire: (expiry: string, type: string) => void;
    consentData: (config: ConsentConfig) => void;
    prevStep: () => void;
}

function ConsentConfiguration({ handleExpire, consentData, prevStep }: ConsentConfigurationProps) {
    const { t } = useTranslation();
    const [consentExpiry, setConsentExpiry] = useState<string>('months6');
    const [bannerLayout, setBannerLayout] = useState<string>('Box');
    const [bannerPosition, setBannerPosition] = useState<string>('leftBottomPopUp');
    const [consentConfig, setConsentConfig] = useState<ConsentConfig>(finalConsentConfig);

    const handleConsentExpire = (value: string | null, option: any) => {
        if (value) {
            setConsentExpiry(value);
            handleExpire(value, 'consent');
        }
    };

    const saveConsentBannerConfig = () => {
        try {
            handleExpire(consentExpiry, "consentExpire");
            const updatedConfig = {
                ...consentConfig,
                layout: bannerLayout,
                alignment: bannerPosition,
                theme: themesConfig[0],
                type: "Light",
                bannerContent: defaultBannerContent
            };
            setConsentConfig(updatedConfig);
            consentData(updatedConfig);
        } catch (error) {
            console.error('Error saving consent banner config:', error);
        }
    };

    return (
      <div>
        <Title ta="center" order={2} mt="lg" fw={500}>{t('consentConfig.title')}</Title>
        <Stack  gap={10} p="lg" pl="xl" pr="xl">
            <Title order={6} fw={500}>{t('consentConfig.consentExpiry')}</Title>
            <Select
                value={consentExpiry}
                onChange={handleConsentExpire}
                data={[
                    { value: 'months3', label: t('consentConfig.months3') },
                    { value: 'months6', label: t('consentConfig.months6') },
                    { value: 'year1', label: t('consentConfig.year1') }
                ]}
            />
        
            <Title order={6} fw={500}>{t('consentConfig.bannerLayout')}</Title>
            <Grid>
            {layouts.map((layout: { layoutType: string; bannerValue: string; styles: React.CSSProperties; text: string }, index: number) => (
                <Grid.Col key={index} span={{ base: 12, md: 4, lg: 4 }}>
                <div>
                    <Paper
                    shadow="0px"
                    withBorder
                    p="lg"
                    style={{
                        position: 'relative',
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        minHeight: '80px',
                        height: '100%',
                    }}
                    onClick={() => {
                        setBannerLayout(layout.layoutType);
                        setBannerPosition(layout.bannerValue);
                        setConsentConfig(prev => ({
                            ...prev,
                            layout: layout.layoutType,
                            alignment: layout.bannerValue
                        }));
                    }}
                    >
                    <div
                        className="fill-primary"
                        style={layout.styles}
                    ></div>
                    {bannerLayout === layout.layoutType && (
                        <IconCircleCheckFilled
                        className="primary"
                        size="1rem"
                        stroke={3}
                        style={{ left: "-8px", top: "-7px", position: "absolute" }}
                        />
                    )}
                    </Paper>
                    <Text size="xs" mt={5} ta="center">
                    {layout.text === 'Box' && t('consentBanner.bannerConfiguration.box')}
                    {layout.text === 'Banner' && t('consentBanner.bannerConfiguration.banner')}
                    {layout.text === 'Popup' && t('consentBanner.bannerConfiguration.popup')}
                    </Text>
                </div>
                </Grid.Col>
            ))}
            </Grid>
            {(bannerLayout === "Box" || bannerLayout === "Banner") && (
            <>
                <Text size="xs" mb="xs" fw={500}>{t('consentBanner.bannerConfiguration.alignment')}{" "}</Text>
                <Radio.Group mb={15} value={bannerPosition}
                onChange={(value: string) => {
                    setBannerPosition(value);
                    setConsentConfig(prev => ({
                        ...prev,
                        alignment: value
                    }));
                }}>
                <SimpleGrid cols={2}>
                    
                    {bannerLayout === "Box" && (
                    <>
                        <Radio size="xs" value="leftBottomPopUp" label={t('consentBanner.bannerConfiguration.bottomLeft')} />
                        <Radio size="xs" value="rightBottomPopUp" label={t('consentBanner.bannerConfiguration.bottomRight')} />
                        <Radio size="xs" value="leftTopPopUp" label={t('consentBanner.bannerConfiguration.topLeft')} />
                        <Radio size="xs" value="rightTopPopUp" label={t('consentBanner.bannerConfiguration.topRight')} />
                    </>
                    )}
                    {bannerLayout === "Banner" && (
                    <>
                        <Radio size="xs" value="bannerTop" label={t('consentBanner.bannerConfiguration.top')} />
                        <Radio size="xs" value="bannerBottom" label={t('consentBanner.bannerConfiguration.bottom')} />
                    </>
                    )}
                </SimpleGrid>
                </Radio.Group>
            </>
            )}
            
            <Group justify="apart" grow>
                <Button variant="default" onClick={prevStep}>{t('integrationPopUp.back')}</Button>
                <Button onClick={(event) => {
                    saveConsentBannerConfig();
                  }}
                > {t('integrationPopUp.next')}</Button>
            </Group>
        </Stack>
      </div>
    );
  }

  export { ConsentConfiguration };